import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/use-toast';
import { useRestaurant } from '../../context/RestaurantContext';
import { ShoppingBag, Plus, ChevronLeft, FileText } from 'lucide-react';
import { listPurchases } from '../../services/purchaseService';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Purchase: the per-location list of supplier purchases, with a New Purchase action. */
export default function PurchaseHome() {
  const navigate = useNavigate();
  const { restaurantId: urlRestaurantId, locationId } = useParams();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = urlRestaurantId || ctxRestaurantId;
  const { toast } = useToast();

  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!restaurantId || !locationId) return;
    setLoading(true);
    try {
      const rows = await listPurchases(restaurantId, locationId);
      setPurchases(rows);
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to load purchases', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId, toast]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 w-full">
      <button
        onClick={() => navigate(`/restaurant/${restaurantId}/purchase`)}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
      >
        <ChevronLeft className="h-4 w-4" /> Locations
      </button>

      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-semibold text-foreground tracking-tight leading-none">Purchase</h1>
        </div>
        <Button onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/purchase/new`)}>
          <Plus className="h-4 w-4 mr-2" /> New Purchase
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShoppingBag className="h-5 w-5" /> Purchases</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-center">Items</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
                )}
                {!loading && purchases.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No purchases yet. Record your first one.</TableCell></TableRow>
                )}
                {!loading && purchases.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="whitespace-nowrap">{fmtDate(p.purchase_date)}</TableCell>
                    <TableCell className="font-medium">{p.invoice_number}</TableCell>
                    <TableCell>{p.supplier || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="capitalize">{p.purchase_type}</TableCell>
                    <TableCell className="text-center tabular-nums">{p.line_count}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMoney(p.total_cost)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/purchase/${p.id}`)}>
                        <FileText className="h-4 w-4 mr-1" /> View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
